import { useEffect, useRef, useState, useCallback } from 'react';
// @ts-ignore
import Soundfont from 'soundfont-player';
import { TabNote } from '@/types/tablatures';
import { TabElement } from '@/components/tablatures/tabConverter';

interface UseHarmonicaAudioProps {
    onEnded?: () => void;
}

export function useHarmonicaAudio({ onEnded }: UseHarmonicaAudioProps = {}) {
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentIndices, setCurrentIndices] = useState<number[]>([]);
    const [tempo, setTempo] = useState(400); // ms per quarter note/beat
    const [isReady, setIsReady] = useState(false);

    const audioCtxRef = useRef<AudioContext | null>(null);
    const instrumentRef = useRef<any>(null);
    const stopRequestedRef = useRef(false);
    const isPlayingRef = useRef(false);

    // Initialize AudioContext
    useEffect(() => {
        if (typeof window !== 'undefined' && !audioCtxRef.current) {
            const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
            audioCtxRef.current = new AudioContext();
        }
        return () => {
            if (audioCtxRef.current) {
                audioCtxRef.current.close().catch(e => console.warn('Error closing AudioContext', e));
                audioCtxRef.current = null;
            }
        };
    }, []);

    // Load Soundfont
    const loadInstrument = useCallback(async () => {
        if (!audioCtxRef.current) return;
        if (instrumentRef.current) return;

        try {
            // Using MusyngKite harmonica soundfont as in original player
            instrumentRef.current = await Soundfont.instrument(audioCtxRef.current, 'harmonica', {
                soundfont: 'MusyngKite',
                format: 'mp3',
                nameToUrl: (name: string, soundfont: string, format: string) =>
                    `https://gleitz.github.io/midi-js-soundfonts/${soundfont}/${name}-${format}.js`
            });
            setIsReady(true);
        } catch (error) {
            console.error('Failed to load instrument:', error);
        }
    }, []);

    const stop = useCallback(() => {
        stopRequestedRef.current = true;
        isPlayingRef.current = false;
        setIsPlaying(false);
        setCurrentIndices([]);
        if (instrumentRef.current) {
            instrumentRef.current.stop();
        }
    }, []);

    const sleep = (ms: number) => new Promise(r => setTimeout(r, Math.max(ms, 10))); // Safe minimum sleep

    // Note mapping logic from player.html
    const holeToNote: Record<number, { blow: string; draw: string }> = {
        1: { blow: 'C4', draw: 'D4' },
        2: { blow: 'E4', draw: 'G4' },
        3: { blow: 'G4', draw: 'B4' },
        4: { blow: 'C5', draw: 'D5' },
        5: { blow: 'E5', draw: 'F5' },
        6: { blow: 'G5', draw: 'A5' },
        7: { blow: 'C6', draw: 'B5' },
        8: { blow: 'E6', draw: 'D6' },
        9: { blow: 'G6', draw: 'F6' },
        10: { blow: 'C7', draw: 'A6' }
    };

    const normalizeNoteToken = (tokenObj: TabElement) => {
        const key = String(tokenObj.key);
        // Handle chords or special groupings if necessary, but key here is usually single note
        // The original logic handled "3..", "(3 4)" etc. 
        // TabConverter splits these into individual notes already?
        // Let's assume TabConverter provides single notes.
        // We need to handle duration though. '..' means hold.
        // TabConverter uses 'type' for dots.

        return {
            hole: parseInt(key),
            isDraw: tokenObj.sign === '-',
            semis: tokenObj.alt || 0,
            overblow: tokenObj.type.includes('o'),
            isHold: tokenObj.type.includes('..')
        };
    };

    const midiNumberFromNoteName = (name: string) => {
        const names: Record<string, number> = { C: 0, 'C#': 1, Db: 1, D: 2, 'D#': 3, Eb: 3, E: 4, F: 5, 'F#': 6, Gb: 6, G: 7, 'G#': 8, Ab: 8, A: 9, 'A#': 10, Bb: 10, B: 11 };
        const m = name.match(/^([A-G][#b]?)(-?\d+)$/);
        if (!m) return null;
        const pitchClass = names[m[1]];
        const octave = parseInt(m[2], 10);
        return pitchClass + (octave + 1) * 12;
    };

    const noteNameFromMidi = (m: number) => {
        const names = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
        const pc = m % 12;
        const oct = Math.floor(m / 12) - 1;
        return names[pc] + oct;
    };

    const computeNoteName = (hole: number, isDraw: boolean, semis: number) => {
        if (!hole || !holeToNote[hole]) return null;
        let baseName = isDraw ? holeToNote[hole].draw : holeToNote[hole].blow;
        let midi = midiNumberFromNoteName(baseName);
        if (midi === null) return null;

        // Simplified bending logic
        if (isDraw && semis > 0) midi -= semis;
        if (!isDraw && semis > 0) midi -= semis; // blow bends

        return noteNameFromMidi(midi);
    };

    const play = useCallback(async (notes: TabNote[]) => {
        if (isPlayingRef.current) return;
        if (!audioCtxRef.current) return;

        try {
            await audioCtxRef.current.resume();
        } catch (e) {
            console.warn('AudioContext resume failed', e);
        }

        if (!instrumentRef.current) {
            await loadInstrument();
        }

        stopRequestedRef.current = false;
        isPlayingRef.current = true;
        setIsPlaying(true);

        const playableNotes = notes.filter(n => (n as TabElement).index !== -1).sort((a, b) => (a as TabElement).index - (b as TabElement).index);

        for (let i = 0; i < notes.length; i++) {
            if (stopRequestedRef.current) break;

            const element = notes[i] as TabElement;

            // Skip structural elements but handle explicit pauses (wait)
            if (['|', '|:', ':|', '||'].includes(element.key)) {
                continue;
            }

            // SPACE: Visual only, skip immediately
            if (element.key === 'SPACE') continue;

            // Pause tokens: _ (underscore) and / (slash/breaker) => sleep tempo
            if (element.key === '_' || element.key === '/') {
                await sleep(tempo);
                continue;
            }

            // CHORD START: (
            if (element.key === '(') {
                // Look ahead for chord members until )
                const chordNotes: TabElement[] = [];
                let j = i + 1;
                while (j < notes.length) {
                    const nextEl = notes[j] as TabElement;
                    if (nextEl.key === ')') {
                        // End of chord
                        i = j; // Advance main loop
                        break;
                    }
                    if (nextEl.key !== 'SPACE' && nextEl.index !== -1) {
                        // Add actionable notes to chord
                        chordNotes.push(nextEl);
                    }
                    j++;
                }

                // Play all collected chord notes simultaneously
                if (chordNotes.length > 0) {
                    // Highlight ALL notes in the chord
                    setCurrentIndices(chordNotes.map(n => n.index));

                    const playPromises = chordNotes.map(n => {
                        const { hole, isDraw, semis, isHold } = normalizeNoteToken(n);
                        // Use longest duration? Or standard tempo?
                        // Assuming chord notes have similar duration or just standard tempo
                        const duration = isHold ? tempo * 2 : tempo;

                        if (!isNaN(hole)) {
                            const noteName = computeNoteName(hole, isDraw, semis);
                            if (noteName && instrumentRef.current && audioCtxRef.current) {
                                try {
                                    return instrumentRef.current.play(noteName, audioCtxRef.current.currentTime, {
                                        duration: duration / 1000,
                                        gain: 1.0 // Full gain for chords? Or reduce to avoid clipping?
                                    });
                                } catch (e) {
                                    console.warn('Play error', e);
                                }
                            }
                        }
                        return Promise.resolve();
                    });

                    await Promise.all(playPromises);
                    await sleep(tempo); // Wait for chord duration
                }
                continue; // Continue main loop
            }

            // IGNORE closing bracket if encountered standalone (shouldn't happen with logic above but safe)
            if (element.key === ')') continue;

            // Single Note Logic (if not part of chord loop above)
            if (element.index !== -1) {
                setCurrentIndices([element.index]);
            }

            const { hole, isDraw, semis, isHold } = normalizeNoteToken(element);
            const duration = isHold ? tempo * 2 : tempo;

            if (!isNaN(hole)) {
                const noteName = computeNoteName(hole, isDraw, semis);
                if (noteName && instrumentRef.current) {
                    try {
                        instrumentRef.current.play(noteName, audioCtxRef.current.currentTime, {
                            duration: duration / 1000,
                            gain: hole >= 7 ? 0.6 : 1.0
                        });
                    } catch (e) {
                        console.warn('Play error', e);
                    }
                }
            }

            await sleep(duration);
        }

        setIsPlaying(false);
        isPlayingRef.current = false;
        setCurrentIndices([]);
        if (onEnded) onEnded();

    }, [tempo, onEnded, loadInstrument]);

    return {
        play,
        stop,
        setTempo,
        isPlaying,
        isReady,
        currentIndices,
        tempo
    };
}
